import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import styled from '@emotion/styled'
import React, { useMemo } from 'react'
import useDrawer from 'modules/drawer/state/use-drawer'
import useConfig from 'modules/config/state/use-config'
import { getAnalyticsInstance } from '../../../analytics/analytics'
import { Hyperlink } from 'modules/hyperlink/hyperlinkComponent'

type ContentPanelConfig = FullImageContentPanelConfig | TopImageContentPanelConfig
interface FullImageContentPanelConfig {
  type: 'FullImage'
  titleLocation: 'top' | 'bottom'
  image: { src: string }
  title: string
}

interface TopImageContentPanelConfig {
  type: 'TopImage'
  image: { src: string }
  title: string
  body: string
  partnerLogos: { src: string; alt: string }[]
}

// =================
// SHARED COMPONENTS
// =================

const ContentPanelContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 25rem;

  @media (min-width: 784px) {
    height: 100%;
  }
`

const Bullet = styled.span<{ isFullImage?: boolean }>`
  margin: 0 8px;
  color: white;
  font-weight: normal;
  color: ${({ isFullImage }) =>
    isFullImage ? 'white' : 'var(--classy-color__neutral--40, rgb(106, 101, 98))'};
`

const FAQOpenerWrapper = styled.div<{ isFullImage?: boolean }>`
  cursor: pointer;
  text-align: center;
  font-family: var(--classy-font__font-family--base);
  font-size: var(--classy-font__label--font-size);
  line-height: var(--classy-font__label--line-height);
  font-weight: var(--classy-font__label--font-weight);
  color: ${({ isFullImage }) =>
    isFullImage ? 'white' : 'var(--classy-color__neutral--40, rgb(106, 101, 98))'};
  font-size: var(--classy-font__label--font-size, 12px);
  line-height: var(--classy-font__label--line-height, 1.5);
  font-weight: var(--classy-font__label--font-weight, 400);
  z-index: 2;

  &:hover {
    text-decoration: underline;
  }

  // Hide it on mobile
  @media (max-width: 784px) {
    display: none;
  }
`

// =====================
// FULL IMAGE COMPONENTS
// =====================

const FullImageContentWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 25rem;

  @media (min-width: 784px) {
    height: 100%;
  }
`

const FullImageTitleWrapper = styled.div<{ position: 'top' | 'bottom' }>`
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  right: 0;
  ${({ position }) => (position === 'top' ? 'height: 12.5rem;' : 'height: 13.5rem;')}
  ${({ position }) => (position === 'top' ? 'top: 0;' : 'bottom: 0;')}
  background: ${({ position }) =>
    position === 'top'
      ? 'linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%)'};
`

const FullImageTitle = styled.div`
  z-index: 1;
  color: white;
  font-size: var(--classy-font__heading-sm--font-size, 28px);
  font-weight: var(--classy-font__heading-sm--font-weight, 700);
  line-height: var(--classy-font__heading-sm--line-height, 120%);
`

const FullImageFAQWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%);
  z-index: 2;
  margin-top: auto;
  padding: 1.5rem 0px;

  @media (max-width: 784px) {
    display: none;
  }
`

// ====================
// TOP IMAGE COMPONENTS
// ====================

const Logo = styled(Image)`
  width: 139px;
  height: 48px;
  object-fit: contain;
`

const TopImageContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem 2rem 1rem 2rem;
  width: 100%;
  height: 100%;
  max-width: 800px;
`

const TopImageFAQContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--classy-color__background, white);
  color: var(--classy-color__neutral--40, rgb(106, 101, 98));
  font-size: var(--classy-font__label--font-size, 12px);
  line-height: var(--classy-font__label--line-height, 1.5);
  font-weight: var(--classy-font__label--font-weight, 400);
`

const TopImageLogoContainer = styled.div`
  text-align: center;
  padding: 0.5rem 0.5rem 0 0.5rem;
  flex-wrap: wrap;
`

const TopImageLogosWrapper = styled.div<{ justify: 'start' | 'center' }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${(props) => props.justify};
  align-items: flex-start;
  margin-top: auto;
  margin-bottom: 1rem;
  gap: 4px;
`

const TopImageParagraph = styled.div`
  margin-top: 6px;
  text-overflow: ellipsis;
  color: var(--classy-theme-color__text-general, black);
  font-size: var(--classy-theme-font__paragraph-md--font-size, 16px);
  font-weight: var(--classy-theme-font__paragraph-md--font-weight, 400);
  line-height: var(--classy-theme-font__paragraph-md--line-height, 150%);
`

const TopImageTitle = styled.div`
  margin-top: 1rem;
  color: var(--classy-theme-color__text-general, black);
  font-size: var(--classy-font__heading-sm--font-size, 28px);
  font-weight: var(--classy-font__heading-sm--font-weight, 700);
  line-height: var(--classy-font__heading-sm--line-height, 120%);
`

const TopImageWrapper = styled.div`
  max-width: 31rem;
  overflow: hidden;
  border-radius: 8px;
`

// =============
// CONTENT PANEL
// =============

const ContentPanel = () => {
  const { t } = useTranslation()
  const config = useConfig()
  const drawer = useDrawer()
  const { contentPanel } = config

  const contentPanelConfig = useMemo((): ContentPanelConfig | null => {
    if (!contentPanel) return null

    const imageSrc = contentPanel.image?.src || ''
    const commonConfig = { image: { src: imageSrc }, title: contentPanel.title }

    if (contentPanel.type === 'FullImage') {
      return {
        type: 'FullImage',
        titleLocation: contentPanel.titleLocation,
        ...commonConfig,
      }
    }

    if (contentPanel.type === 'TopImage') {
      return {
        type: 'TopImage',
        body: contentPanel.body,
        partnerLogos: contentPanel.partnerLogos || [],
        ...commonConfig,
      }
    }

    return null
  }, [contentPanel])

  const handleFaqOpen = () => {
    // Track FAQ click event
    getAnalyticsInstance().track('studio_faq_open')

    drawer.open('faq')
  }

  const FAQOpener = ({ isFullImage = false }: { isFullImage?: boolean }) => (
    <FAQOpenerWrapper isFullImage={isFullImage} onClick={handleFaqOpen}>
      {t('ContentPanel.faqOpener.label', 'Frequently asked questions')}
    </FAQOpenerWrapper>
  )

  // Render either the full image content or the top image content panel
  const renderContentPanel = () => {
    if (!contentPanelConfig) return null

    // Content Panel | FULL IMAGE
    if (contentPanelConfig.type === 'FullImage') {
      const { titleLocation } = contentPanelConfig

      return (
        <FullImageContentWrapper>
          <Image
            src={contentPanelConfig.image.src}
            alt={contentPanelConfig.title}
            layout="fill"
            objectFit="cover"
            quality={100}
            priority
          />
          <FullImageTitleWrapper position={titleLocation}>
            <FullImageTitle>{contentPanelConfig.title}</FullImageTitle>
          </FullImageTitleWrapper>

          <FullImageFAQWrapper>
            <FAQOpener isFullImage />
            {config?.hyperlink && config?.hyperlink.isEnabled && (
              <>
                <Bullet isFullImage>•</Bullet>
                <Hyperlink
                  isEnabled={config?.hyperlink.isEnabled}
                  url={config?.hyperlink?.url}
                  text={config?.hyperlink?.text}
                  styles={{
                    fontSize: 'var(--classy-font__label--font-size, 12px)',
                    lineHeight: 'var(--classy-font__label--line-height, 1.5)',
                    fontWeight: 'var(--classy-font__label--font-weight, 400)',
                  }}
                />
              </>
            )}
          </FullImageFAQWrapper>
        </FullImageContentWrapper>
      )
    }

    // Content Panel | TOP IMAGE
    if (contentPanelConfig.type === 'TopImage') {
      return (
        <TopImageContentWrapper>
          <TopImageWrapper>
            <Image
              src={contentPanelConfig.image.src}
              alt={contentPanelConfig.title}
              layout="responsive"
              width={800}
              height={450}
              objectFit="cover"
              quality={100}
              priority
              style={{ borderRadius: '8px' }}
            />
          </TopImageWrapper>

          <TopImageTitle>{contentPanelConfig.title}</TopImageTitle>
          <TopImageParagraph>{contentPanelConfig.body}</TopImageParagraph>

          {/* Render partner logos dynamically from the array */}
          <TopImageLogosWrapper
            justify={contentPanelConfig.partnerLogos.length > 2 ? 'start' : 'center'}
          >
            {contentPanelConfig.partnerLogos
              .filter((logo) => logo.src)
              .map((logo, index: number) => (
                <TopImageLogoContainer key={index}>
                  <Logo
                    width={154}
                    height={48}
                    src={logo.src}
                    alt={
                      logo.alt
                        ? logo.alt
                        : t(
                            'ContentPanel.topImage.partnerLogoFallbackAlt',
                            'Partner Logo {{index}}',
                            { index: index + 1 },
                          )
                    }
                  />
                </TopImageLogoContainer>
              ))}
          </TopImageLogosWrapper>

          <TopImageFAQContainer>
            <FAQOpener />
            {config?.hyperlink && config?.hyperlink.isEnabled && (
              <>
                <Bullet>•</Bullet>
                <Hyperlink
                  isEnabled={config?.hyperlink.isEnabled}
                  url={config?.hyperlink?.url}
                  text={config?.hyperlink?.text}
                  textColor="var(--classy-color__neutral--40, rgb(106, 101, 98))"
                  styles={{
                    fontSize: 'var(--classy-font__label--font-size, 12px)',
                    lineHeight: 'var(--classy-font__label--line-height, 1.5)',
                    fontWeight: 'var(--classy-font__label--font-weight, 400)',
                  }}
                />
              </>
            )}
          </TopImageFAQContainer>
        </TopImageContentWrapper>
      )
    }

    return null
  }

  return (
    <ContentPanelContainer data-testid="content-panel">
      {renderContentPanel()}
    </ContentPanelContainer>
  )
}

ContentPanel.displayName = 'ContentPanel'

export default React.memo(ContentPanel)
